import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Grid, Stack, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import urlConstant from "../../constants/urlConstants";
import dataConstants from "../../constants/dataConstants";
import { httpGet, httpPost } from "../../services/commonService";
import DataCardGrid from "../../shared-components/DataCardGrid/DataCardGrid";

const WalletCard = ({ cardId, amount, label, imageName }) => (
  <Card
    sx={{
      backgroundImage: `url(${process.env.REACT_APP_BLOB_URL}/backoffice-assets/background-img.jpg)`,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 0,
      width: "100%",
      flexDirection: "column",
      height: "100%",
      p: {
        xs: "15px",
        sm: "20px",
      },
    }}
  >
    <Card
      sx={{
        backgroundImage: `url(${process.env.REACT_APP_BLOB_URL}${imageName})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        borderRadius: "20px",
        display: "flex",
        justifyContent: "space-between",
        gap: 0,
        width: "100%",
        flexDirection: "column",
        height: "auto",
        padding: "20px",
        aspectRatio: "3.5/2",
      }}
    >
      <MoreVertIcon sx={{ opacity: "0.5" }} fontSize="large" />
      <Typography color="common.white" variant="h6" fontWeight={600}>
        {cardId}
      </Typography>
    </Card>

    <Stack
      direction={"row"}
      gap={1}
      width={"100%"}
      mt={5}
      alignItems={"center"}
      justifyContent={"space-between"}
      flexWrap={"wrap"}
    >
      <Button
        sx={(theme) => ({
          borderRadius: "25px",
          py: 0.5,
          color: theme.palette.secondary.main,
          fontSize: {
            xs: "12px",
            sm: "16px",
          },
          backgroundColor: "common.white",
          color: "common.black"
        })}
        variant="outlined"
        color="secondary"
      >
        {label}
      </Button>
      <Typography
        color="common.white"
        variant="h3"
        fontWeight={600}
        sx={{
          fontSize: {
            xs: "20px",
            sm: "36px",
          },
        }}
      >
        {amount}
      </Typography>
    </Stack>
  </Card>
);

const IWallet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [walletCards, setWalletCards] = useState([]);
  const [totalBalance, setTotalBalance] = useState(0);

  useEffect(() => {
    fetchWalletSummary();
  }, []);

  const transformToWalletCards = (data) => {
    const symbol = data.CurrencySymbol || "$";
    const format = (val) =>
      `${symbol}${(val ?? 0).toFixed(dataConstants.TwoDigitConstValue)}`;

    return [
      {
        cardId: "",
        amount: format(data.Balance),
        label: t("IWallet.Pending Balance"),
        imageName: "/backoffice-assets/iwallet-availables-funds.png",
      },
      {
        cardId: "",
        amount: format(data.TotaliWalletbalance),
        label: t("IWallet.Available Funds"),
        imageName: "/backoffice-assets/iwallet-pending-balance.png",
      },
      {
        cardId: "",
        amount: format(data.PendingPayout),
        label: t("IWallet.Pending Payouts"),
        imageName: "/backoffice-assets/iwallet-pending-payouts.png",
      },
    ];
  };

  const fetchWalletSummary = async () => {
    try {
      const response = await httpGet(
        urlConstant.iWallet.GetiWalletSummary,
        true,
        true,
        accessToken,
        impersonateEmail
      );

      const summaryData = response?.Value?.Data;
      if (summaryData) {
        const cards = transformToWalletCards(summaryData);
        setWalletCards(cards);
        setTotalBalance(summaryData.TotaliWalletbalance ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch wallet summary:", error);
    }
  };

  const columns = [
    {
      key: "iWalletId",
      label: t("IWallet.Id"),
      type: "number",
      isFilterVisible: true,
    },
    {
      key: "StrTransactionDate",
      label: t("IWallet.Date"),
      type: "date",
      isFilterVisible: true,
    },
    {
      key: "Credits",
      label: t("IWallet.Credits"),
      type: "money",
    },
    {
      key: "Debits",
      label: t("IWallet.Debits"),
      type: "money",
    },
    {
      key: "AccountName",
      label: t("IWallet.Customer Name"),
      type: "text",
    },
    {
      key: "OrderID",
      label: t("IWallet.Order Number"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "iWalletStatus",
      label: t("IWallet.Status"),
      type: "text",
      isFilterVisible: true,
      statusIcon: true,
    },
    {
      key: "TransactionType",
      label: t("IWallet.Transaction Type"),
      type: "text",
      isFilterVisible: true,
    },
    {
      key: "Description",
      label: t("IWallet.Description"),
      type: "text",
    },
  ];

  const fetchDataFunction = useCallback(
    async ({ page, pageSize, search, filters, sortKey, sortDirection }) => {
      const userIwalletListUrl = urlConstant.iWallet.IWalletListByUserIdWithSP;
      const pagingInfo = {
        PageNumber: page,
        RowsPerPage: pageSize,
        CommonSearchText: search,
        SortColumns: {
          SortColumnName: sortKey,
          SortOrder: sortDirection,
        },
        Filter: {
          iWalletId: filters?.iWalletId,
          TransactionDate: filters?.StrTransactionDate,
          iWalletStatus: filters?.iWalletStatus,
          TransactionType: filters?.TransactionType,
          OrderId: filters?.OrderID,
        },
      };
      const requestBody = { PagingInfoVM: pagingInfo };
      try {
        const response = await httpPost(
          userIwalletListUrl,
          requestBody,
          true,
          false,
          accessToken,
          impersonateEmail
        );
        if (
          response?.Value?.Status === dataConstants.APIResponseStatus.Success
        ) {
          return {
            data: response.Value.Data.Data,
            totalCount: response.Value.Data.TotalCount,
          };
        } else {
          return { data: [], totalCount: 0 };
        }
      } catch (err) {
        console.error(err);
      }
      return { data: [], totalCount: 0 };
    },
    [accessToken, impersonateEmail]
  );

  return (
    <Box flexDirection={"column"} gap={2} display={"flex"}>
      <Typography color="secondary.light" variant="h4" fontWeight={600}>
        {t("My Account")}
      </Typography>

      <Box width={"100%"}>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            border: "1px solid var(--headerbg)",
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Stack gap={"20px"}>
            <Stack
              direction={"row"}
              gap={1}
              width={"100%"}
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
            >
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("IWallet.IWallet")}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  sessionStorage.setItem("iWalletBalance", totalBalance);
                  navigate("/withDrawFunds", {
                    state: { iWalletBalance: totalBalance },
                  });
                }}
                sx={{
                  px: 3,
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
              >
                {t("IWallet.Withdraw Funds")}
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {walletCards.map(
                ({ cardId, amount, label, imageName }, index) => (
                  <Grid key={index} size={{ xs: 12, md: 6, lg: 4 }}>
                    <WalletCard
                      cardId={cardId}
                      amount={amount}
                      label={label}
                      imageName={imageName}
                    />
                  </Grid>
                )
              )}
            </Grid>
            <DataCardGrid
              onFetchData={fetchDataFunction}
              columns={columns}
              defaultItemsPerPage={8}
              defaultColumnsPerGrid={3}
              enableExport={true}
              enablePagination={true}
              uniqueId="iwallet-grid"
            />
          </Stack>
        </Card>
      </Box>
    </Box>
  );
};

export default IWallet;
